import cn from "classnames";
import { Button } from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";
import { useFetchSessionNote } from "../../../hooks/queries/note";
import ListState from "../../ListState/ListState";
import AppointmentDetails from "../AppointmentDetails/AppointmentDetails";
import EditCancellation from "./EditCancellation/EditCancellation";
import EditRecordOfDisclosure from "./EditRecordOfDisclosure/EditRecordOfDisclosure";
import EditSoapNote from "./EditSoapNote/EditSoapNote";
import EditSupervision from "./EditSupervision/EditSupervision";
import EditTermination from "./EditTermination/EditTermination";
import EditSafetyPlan from "./EditSafetyPlan/EditSafetyPlan";
import EditTreatmentReview from "./EditTreatmentReview/EditTreatmentReview";
import EditIndividualIntake from "./EditIndividualIntake/EditIndividualIntake";
import EditFamilyIntake from "./EditFamilyIntake/EditFamilyIntake";
import EditGeneralNote from "./EditGeneralNote/EditGeneralNote";
import EditSupervisorCheckInNote from "./EditSupervisorCheckInNote/EditSupervisorCheckInNote";
import { useAppSelector } from "../../../hooks/useRedux";
import EditPauseNotification from "./EditPauseNotification/EditPauseNotification";
import {
    NoteStatus,
    NoteTypes,
} from "../../../modules/notes/types/notes.types";
import {
    NOTE_EDIT_CONFLICT_MESSAGE,
    NoteEditProvider,
    isNewerNoteVersion,
    useNoteEditContext,
} from "../../../modules/notes/context/notes.context";
import Loader from "../../../shared-ui/Loader/Loader";
import InfoIcon from "../../../icons/Info";
import EditStandardNoteTemplateType from "./EditStandardNoteTemplateType/EditStandardNoteTemplateType";
import EditPauseNotificationPsych from "./EditPauseNotificationPsych/EditPauseNotificationPsych";
import EditNoShowPsychNote from "./EditNoShowPsychNote/EditNoShowPsychNote";
import EditRecordOfDisclosurePsych from "./EditRecordOfDisclosurePsych/EditRecordOfDisclosurePsych";
import EditTerminationOfTreatment from "./EditTerminationOfTreatment/EditTerminationOfTreatment";
import EditInitialDiagnosticEvaluation from "./EditInitialDiagnosticEvaluation/EditInitialDiagnosticEvaluation";
import EditInitialSoapNote from "./EditInitialSoapNote/EditInitialSoapNote";
import EditSupervisorDiagnosticReview from "./EditSupervisorDiagnosticReview/EditSupervisorDiagnosticReview";
import EditFamilyInitialDiagnosticEvaluation from "./EditFamilyInitialDiagnosticEvaluation/EditFamilyInitialDiagnosticEvaluation";
import EditFamilyTreatmentReview from "./EditFamilyTreatmentReview/EditFamilyTreatmentReview";
import EditPsychosocialAssessment from "./EditPsychosocialAssessment/EditPsychosocialAssessment";
import EditFamilyPsychosocialAssessment from "./EditFamilyPsychosocialAssessment/EditFamilyPsychosocialAssessment";
import EditCommunicationWithClient from "./EditCommunicationWithClient/EditCommunicationWithClient";
import EditFormsAndDocumentationNote from "./EditFormsAndDocumentationNote/EditFormsAndDocumentationNote";
import EditConsultationMedNote from "./EditConsultationMedNote/EditConsultationMedNote";
import EditDocumentationRequestNoteMed from "./EditDocumentationRequestNoteMed/EditDocumentationRequestNoteMed";
import EditEvaluationSubmissionNoteMed from "./EditEvaluationSubmissionNoteMed/EditEvaluationSubmissionNoteMed";
import EditNoShowNoteMed from "./EditNoShowNoteMed/EditNoShowNoteMed";
import EditEvaluationManagementAndTherapyNoteMed from "./EditEvaluationManagementAndTherapyNoteMed/EditEvaluationManagementAndTherapyNoteMed";
import EditEvaluationManagementNoteMed from "./EditEvaluationManagementNoteMed/EditEvaluationManagementNoteMed";
import EditTermOfCareNoteMed from "./EditTermOfCareNoteMed/EditTermOfCareNoteMed";
import EditTreatmentReviewNoteMed from "./EditTreatmentReviewNoteMed/EditTreatmentReviewNoteMed";
import EditIntakeNoteMed from "./EditIntakeNoteMed/EditIntakeNoteMed";

function NoteEditStatusBar({ latestVersion }: { latestVersion?: string }) {
    const { autosaveStatus, hasEditConflict, versionRef } =
        useNoteEditContext();
    const { isOpen } = useAppSelector((state) => state.nav);

    // To determine if mutation is currently running, returns 0 || 1
    const isEditDraftNoteMutating = useIsMutating(["edit-note"]);

    const hasConflict =
        hasEditConflict ||
        (isEditDraftNoteMutating === 0 &&
            isNewerNoteVersion(latestVersion, versionRef?.current));

    if (isEditDraftNoteMutating === 0 && !autosaveStatus && !hasConflict) {
        return null;
    }

    const renderStatus = () => {
        if (hasConflict) {
            return (
                <div
                    role="alert"
                    className="flex items-center gap-2 text-danger font-medium"
                >
                    <InfoIcon className="shrink-0" />
                    {NOTE_EDIT_CONFLICT_MESSAGE}
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => window.location.reload()}
                    >
                        Reload
                    </Button>
                </div>
            );
        }

        if (isEditDraftNoteMutating > 0) {
            return (
                <span className="flex items-center text-textLink font-medium">
                    Autosaving...
                </span>
            );
        }

        if (autosaveStatus && "error" in autosaveStatus) {
            return (
                <span
                    role="alert"
                    className="flex items-center text-danger font-medium"
                >
                    <InfoIcon className="mr-1" />
                    {autosaveStatus.error}
                </span>
            );
        }

        return (
            <span className="flex items-center text-textLink font-medium">
                <InfoIcon className="mr-1" />
                {`Autosaved at ${autosaveStatus?.savedAt}`}
            </span>
        );
    };

    return (
        <div
            className={cn(
                "flex items-center justify-end fixed top-10 right-2 z-[2] bg-[--card-bg-dark] backdrop-blur-[20px] py-[--padding-card-y] px-[--padding-card-x] w-[calc(100%-var(--sidebar-width)-16px)]",
                {
                    "!w-full !right-0": !isOpen,
                }
            )}
        >
            {renderStatus()}
        </div>
    );
}

export default function EditNote() {
    // Get id from url params
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    // Hook for fetching note
    const { data, error, isLoading, isSuccess } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    // Determines the edit form to display
    const handleEditNoteFormToShow = () => {
        switch (data?.data.type) {
            case NoteTypes.CANCELLATION_NOTE:
                return <EditCancellation noteDetails={data.data} />;

            case NoteTypes.SUPERVISION_NOTE:
                return <EditSupervision noteDetails={data.data} />;

            case NoteTypes.RECORD_OF_DISCLOSURE:
                return <EditRecordOfDisclosure noteDetails={data.data} />;

            case NoteTypes.FAMILY_SOAP_NOTE:
                return <EditSoapNote noteDetails={data.data} />;

            case NoteTypes.INDIVIDUAL_SOAP_NOTE:
                return <EditSoapNote noteDetails={data.data} />;

            case NoteTypes.TERMINATION_NOTE:
                return <EditTermination noteDetails={data.data} />;

            case NoteTypes.SAFETY_PLAN:
                return <EditSafetyPlan noteDetails={data.data} />;

            case NoteTypes.INDIVIDUAL_TREATMENT_REVIEW:
                return <EditTreatmentReview noteDetails={data.data} />;

            case NoteTypes.FAMILY_TREATMENT_REVIEW:
                return <EditFamilyTreatmentReview noteDetails={data.data} />;

            case NoteTypes.PSYCHOSOCIAL_ASSESSMENT:
                return <EditPsychosocialAssessment noteDetails={data.data} />;

            case NoteTypes.MINOR_PSYCHOSOCIAL_ASSESSMENT:
                return (
                    <EditPsychosocialAssessment
                        noteDetails={data.data}
                        isMinor
                    />
                );

            case NoteTypes.FAMILY_PSYCHOSOCIAL_ASSESSMENT:
                return (
                    <EditFamilyPsychosocialAssessment noteDetails={data.data} />
                );

            case NoteTypes.INDIVIDUAL_INTAKE_NOTE:
                return (
                    <EditIndividualIntake
                        noteDetails={data.data}
                        isMinor={false}
                    />
                );

            case NoteTypes.MINOR_INTAKE_NOTE:
                return <EditIndividualIntake noteDetails={data.data} isMinor />;

            case NoteTypes.FAMILY_INTAKE_NOTE:
                return <EditFamilyIntake noteDetails={data.data} />;

            case NoteTypes.GENERAL_UPDATE_NOTE:
                return <EditGeneralNote noteDetails={data.data} />;
            case NoteTypes.SUPERVISOR_CHECK_IN:
                return <EditSupervisorCheckInNote noteDetails={data.data} />;
            case NoteTypes.PAUSE_NOTIFICATION_NOTE:
                return <EditPauseNotification noteDetails={data.data} />;
            case NoteTypes.ADMINISTRATION_PSYCH:
            case NoteTypes.ADMINISTRATION_TECH:
            case NoteTypes.TESTING_INTAKE_MINOR_INTAKE_PSYCH:
            case NoteTypes.INTEGRATING_PATIENT_DATA_PSYCH:
            case NoteTypes.SCORING_BY_TECH_PSYCH:
            case NoteTypes.SCORING_BY_PSYCH:
            case NoteTypes.INTERPRETATION_REPORT_WRITING_PSYCH:
            case NoteTypes.FEEDBACK_PSYCH:
                return <EditStandardNoteTemplateType noteDetails={data.data} />;
            case NoteTypes.PAUSE_NOTIFICATION_PSYCH:
                return <EditPauseNotificationPsych noteDetails={data.data} />;
            case NoteTypes.NO_SHOW_PSYCH:
                return <EditNoShowPsychNote noteDetails={data.data} />;
            case NoteTypes.RECORD_OF_DISCLOSURE_PSYCH:
                return <EditRecordOfDisclosurePsych noteDetails={data.data} />;
            case NoteTypes.TERMINATION_OF_TREATMENT_PSYCH:
                return <EditTerminationOfTreatment noteDetails={data.data} />;
            case NoteTypes.INITIAL_DIAGNOSTIC_EVALUATION:
                return (
                    <EditInitialDiagnosticEvaluation noteDetails={data.data} />
                );
            case NoteTypes.MINOR_INITIAL_DIAGNOSTIC_EVALUATION:
                return (
                    <EditInitialDiagnosticEvaluation
                        noteDetails={data.data}
                        isMinor
                    />
                );
            case NoteTypes.INITIAL_SOAP_NOTE:
            case NoteTypes.FAMILY_INITIAL_SOAP_NOTE:
                return <EditInitialSoapNote noteDetails={data.data} />;
            case NoteTypes.SUPERVISOR_DIAGNOSTIC_REVIEW:
                return (
                    <EditSupervisorDiagnosticReview noteDetails={data.data} />
                );
            case NoteTypes.FAMILY_INITIAL_DIAGNOSTIC_EVALUATION:
                return (
                    <EditFamilyInitialDiagnosticEvaluation
                        noteDetails={data.data}
                    />
                );
            case NoteTypes.COMMUNICATION_WITH_CLIENT:
                return <EditCommunicationWithClient noteDetails={data.data} />;
            case NoteTypes.FORMS_AND_DOCUMENTATION_NOTE:
                return (
                    <EditFormsAndDocumentationNote noteDetails={data.data} />
                );
            case NoteTypes.CONSULTATION_NOTE_MED:
                return <EditConsultationMedNote noteDetails={data.data} />;

            case NoteTypes.DOCUMENTATION_REQUEST_NOTE_MED:
                return (
                    <EditDocumentationRequestNoteMed noteDetails={data.data} />
                );
            case NoteTypes.EVALUATION_SUBMISSION_NOTE_MED:
                return (
                    <EditEvaluationSubmissionNoteMed noteDetails={data.data} />
                );
            case NoteTypes.NO_SHOW_NOTE_MED:
                return <EditNoShowNoteMed noteDetails={data.data} />;
            case NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED:
                return (
                    <EditEvaluationManagementAndTherapyNoteMed
                        noteDetails={data.data}
                    />
                );
            case NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED:
                return (
                    <EditEvaluationManagementNoteMed noteDetails={data.data} />
                );
            case NoteTypes.TERM_OF_CARE_NOTE_MED:
                return <EditTermOfCareNoteMed noteDetails={data.data} />;
            case NoteTypes.TREATMENT_REVIEW_NOTE_MED:
                return <EditTreatmentReviewNoteMed noteDetails={data.data} />;
            case NoteTypes.INTAKE_NOTE_MED:
                return <EditIntakeNoteMed noteDetails={data.data} />;

            default:
                return null;
        }
    };

    return (
        <>
            {isLoading && (
                <Loader loaderType="section" wrapperClassName="h-[60vh]" />
            )}
            {error && (
                <ListState
                    isError
                    stateHelperText="Please submit a feedback or Try reloading this page"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display edit forms at this time, please try again later`
                    }
                />
            )}
            {isSuccess && data ? (
                <div>
                    {data.data.note_status === NoteStatus.DRAFT ? (
                        <div>
                            <div className="pb-[100px] sm:pb-[44px]">
                                <NoteEditProvider key={noteId}>
                                    <NoteEditStatusBar
                                        latestVersion={
                                            data.data.note_last_modified_at
                                        }
                                    />
                                    <div className="mt-4 pb-[--spacing-items] [border-bottom:1px_solid_var(--color-stroke)] mb-[--spacing-items]">
                                        <AppointmentDetails />
                                    </div>

                                    {handleEditNoteFormToShow()}
                                </NoteEditProvider>
                            </div>
                        </div>
                    ) : (
                        <ListState
                            isError
                            stateHelperText="Please go to the create note screen"
                            errorMsg="You cannot edit this note because it is not a draft note"
                        />
                    )}
                </div>
            ) : null}
        </>
    );
}
